import React, { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { mediaAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Upload, Trash2, Copy, Loader2 } from 'lucide-react';
import { formatDateShort, getImageUrl } from '../../lib/utils';

export const AdminMedia = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const res = await mediaAPI.getAll();
      setMedia(res.data);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await mediaAPI.upload(formData);
      }
      toast.success('Upload successful');
      fetchMedia();
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (id) => {
    try {
      await mediaAPI.delete(id);
      toast.success('Media deleted');
      fetchMedia();
    } catch (error) {
      toast.error('Failed to delete media');
    }
  };

  const handleCopyUrl = (url) => {
    const fullUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL copied to clipboard');
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-media-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold">Media Library</h1>
            <p className="text-muted-foreground">Upload and manage images and videos</p>
          </div>
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleUpload}
              className="hidden"
              data-testid="media-upload-input"
            />
            <Button disabled={uploading} data-testid="upload-media-btn">
              {uploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Upload
            </Button>
          </label>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <Skeleton key={i} className="aspect-square" />
            ))}
          </div>
        ) : media.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No media files yet. Upload some images or videos to get started.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {media.map((item) => (
              <div
                key={item.id}
                className="group relative aspect-square bg-secondary rounded-sm overflow-hidden"
                data-testid={`media-item-${item.id}`}
              >
                {item.type === 'image' ? (
                  <img
                    src={getImageUrl(item.url)}
                    alt={item.filename}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <video
                    src={getImageUrl(item.url)}
                    className="w-full h-full object-cover"
                  />
                )}
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={() => handleCopyUrl(item.url)}
                    data-testid={`copy-url-${item.id}`}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="icon" data-testid={`delete-media-${item.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Media</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this file? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(item.id)}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>

                {/* Info */}
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-2 text-xs text-white">
                  <p className="truncate">{item.filename}</p>
                  <p className="text-white/60">{formatDateShort(item.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMedia;
